/**
 * Scroll utilities composable
 * Manages scrolling to registered elements with reduced motion support
 */

import { ref } from 'vue'

/** Registry of scrollable elements */
const elements = ref<Map<string, HTMLElement>>(new Map())

/** Return type of useScrollTo composable */
export interface UseScrollToReturn {
  register: (id: string, element: HTMLElement | null | undefined) => void
  scrollTo: (id: string, options?: ScrollIntoViewOptions) => void
  scrollToTop: () => void
}

/**
 * Scroll utilities with accessibility support
 * Respects user's reduced motion preference
 * @returns Scroll functions and element registration
 */
export function useScrollTo(): UseScrollToReturn {
  /**
   * Check if user prefers reduced motion
   * @returns True if user has reduced motion preference
   */
  const prefersReducedMotion = (): boolean => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  /**
   * Register element for scroll targeting
   * @param id - Unique identifier for element
   * @param element - HTML element to register (null to unregister)
   */
  const register = (id: string, element: HTMLElement | null | undefined): void => {
    if (element) {
      elements.value.set(id, element)
    } else {
      elements.value.delete(id)
    }
  }

  /**
   * Scroll to registered element
   * @param id - ID of registered element
   * @param options - ScrollIntoView options (behavior, block, inline)
   */
  const scrollTo = (id: string, options: ScrollIntoViewOptions = {}): void => {
    const element = elements.value.get(id)
    if (element) {
      element.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'center',
        ...options,
      })
    }
  }

  /**
   * Scroll to top of page
   * Respects reduced motion preference
   */
  const scrollToTop = (): void => {
    window.scrollTo({
      top: 0,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    })
  }

  return {
    register,
    scrollTo,
    scrollToTop,
  }
}